/**
 * logEntries reads/writes — port of LogEntryActions / QuickMacrosActions /
 * FirebaseActionsTabSearched / FirebaseActionsRecipes / FirebaseActionsVoreRecipes
 * on the Firebase JS SDK. Same doc shapes, same ids, same recentlyAdded side-effects.
 */
import {
  collection, doc, getDoc, getDocs, limit, onSnapshot, query, setDoc, updateDoc, deleteDoc, where, writeBatch,
} from 'firebase/firestore'
import moment from 'moment'
import { db } from '@/lib/firebase'
import { track } from '@/lib/analytics'
import { randomString } from '@/shared/utilities/FirebaseHelper'
import { stripUndefined } from '@/lib/format'
import { updateStreak } from './streak'
import { LOG_CATEGORY, type FoodEntry, type LogEntry, type MacroSet, type AIIngredient } from '@/types/entries'

const entriesCol = () => collection(db, 'logEntries')

/** Live uid-wide subscription (the Log screen's `allLogEntries`, reused for the calendar dots + progress). */
export function subscribeAllEntries(uid: string, onChange: (entries: LogEntry[]) => void, onError?: (e: unknown) => void): () => void {
  const q = query(entriesCol(), where('uid', '==', uid))
  return onSnapshot(
    q,
    (snap) => onChange(snap.docs.map((d) => ({ ...(d.data() as LogEntry), id: d.id }))),
    (err) => {
      console.error('subscribeAllEntries', err)
      onError?.(err)
    },
  )
}

/** Save a new non-food entry (mood / symptom / med / exercise / water / note). Honors a caller-supplied id. */
export async function saveLogEntry(uid: string, obj: Partial<LogEntry> & { cat: number; time: string }): Promise<string> {
  const id = obj.id || randomString(14)
  const timestamp = moment(obj.time).toISOString()
  const entry = stripUndefined({ ...obj, id, uid, time: timestamp, date: obj.date || moment(timestamp).format('YYYY-MM-DD') })
  await setDoc(doc(entriesCol(), id), entry)
  track('log_entry_created', { entry_type: catString(obj.cat), time_of_day: moment(timestamp).format('HH') })
  await updateStreak(uid, timestamp)
  return id
}

export async function updateLogEntry(uid: string, id: string, newObj: Partial<LogEntry>): Promise<void> {
  const updated: Record<string, unknown> = { ...newObj, uid }
  if (newObj.date) updated.date = newObj.date
  else if (newObj.time != null) updated.date = moment(newObj.time).format('YYYY-MM-DD')
  await updateDoc(doc(entriesCol(), id), stripUndefined(updated))
}

export async function deleteLogEntry(id: string): Promise<void> {
  const snap = await getDoc(doc(entriesCol(), id))
  const data = snap.data() as LogEntry | undefined
  await deleteDoc(doc(entriesCol(), id))
  track('log_entry_deleted', { entry_type: catString(data?.cat), deletion_method: 'confirmed' })
}

/** Water: one doc per day with a deterministic id, value in ml. */
export async function upsertWater(uid: string, date: string, valueMl: number, existingId?: string | null): Promise<void> {
  if (existingId) {
    if (valueMl <= 0) return deleteDoc(doc(entriesCol(), existingId))
    return updateDoc(doc(entriesCol(), existingId), { value: valueMl, date })
  }
  if (valueMl <= 0) return
  const id = `${uid}-water-${date}`
  const time = `${date}T12:00:00Z`
  await setDoc(doc(entriesCol(), id), { id, uid, date, time, cat: LOG_CATEGORY.WATER, value: valueMl })
  // Same as mobile saveLogEntry: the entry's own timestamp drives the today-only streak logic.
  await updateStreak(uid, time)
}

/** Daily note: one doc per day with a deterministic id; empty text deletes. */
export async function upsertNote(uid: string, date: string, text: string, existing?: { id: string; time: string; date: string } | null): Promise<void> {
  if (!text) {
    if (existing) await deleteDoc(doc(entriesCol(), existing.id))
    return
  }
  if (existing) return updateDoc(doc(entriesCol(), existing.id), { value: text, time: existing.time, date: existing.date })
  const id = `${uid}-note-${date}`
  const time = `${date}T12:00:00Z`
  await setDoc(doc(entriesCol(), id), { id, uid, date, time, cat: LOG_CATEGORY.NOTE, value: text })
  await updateStreak(uid, time)
}

export interface FoodToLog {
  food_id: string
  food_name: string
  brand_name: string
  type: FoodEntry['type']
  image_url?: string
  isAIScan?: boolean
  source?: string
  ingredients?: AIIngredient[]
  /** Vore catalog recipes are intentionally NOT added to recentlyAdded. */
  skipRecent?: boolean
}

/**
 * Log a food (searched API food, My Foods preset, user recipe, Vore recipe)
 * for a serving — writes the entry and the recentlyAdded row in one batch.
 */
export async function logFood(uid: string, food: FoodToLog, serving: MacroSet, servingDesc: string, timeIso: string, editingId?: string | null): Promise<string> {
  const id = editingId || randomString(14)
  const timestamp = moment(timeIso).toISOString()
  const postData: FoodEntry = stripUndefined({
    id,
    food_id: food.food_id,
    food_name: food.food_name,
    brand_name: food.brand_name,
    serving_desc: servingDesc,
    calories: num(serving.calories),
    fats: num(serving.fats),
    proteins: num(serving.proteins),
    totalcarbs: num(serving.totalcarbs),
    fiber: num(serving.fiber),
    nutrients: serving.nutrients || {},
    type: food.type,
    random: id,
    uid,
    cat: 0,
    date: moment(timestamp).format('YYYY-MM-DD'),
    time: timestamp,
    notes: '',
    image_url: food.image_url || '',
    ...(food.isAIScan ? { isAIScan: true, source: food.source, ingredients: food.ingredients || [] } : {}),
  }) as FoodEntry

  const batch = writeBatch(db)
  batch.set(doc(entriesCol(), id), postData)
  if (!food.skipRecent) {
    batch.set(
      doc(db, 'recentlyAdded', uid),
      {
        [food.food_id]: stripUndefined({
          food_name: food.food_name,
          brand_name: food.brand_name,
          food_id: food.food_id,
          type: food.type,
          serving_size: servingDesc,
          calories: postData.calories,
          fats: postData.fats,
          proteins: postData.proteins,
          totalcarbs: postData.totalcarbs,
          fiber: postData.fiber,
          timestamp,
          ...(food.image_url ? { image_url: food.image_url } : {}),
        }),
      },
      { merge: true },
    )
  }
  await batch.commit()
  track('log_entry_created', { entry_type: 'food', food_source: food.type, time_of_day: moment(timestamp).format('HH') })
  await updateStreak(uid, timestamp)
  return id
}

export interface QuickMacrosForm {
  name: string
  servingsize: string
  calories: number | string
  fats: number | string
  proteins: number | string
  totalcarbs: number | string
  fiber: number | string
  nutrients?: Record<string, number>
  image_url?: string
  isAIScan?: boolean
  source?: 'camera' | 'voice' | 'label'
  ingredients?: AIIngredient[]
  food_id?: string
}

/** Quick Macros / AI scan result → one food entry (port of logQuickMacros). */
export async function logQuickMacros(uid: string, form: QuickMacrosForm, timeIso: string, editingId?: string | null): Promise<string> {
  const id = editingId || randomString(14)
  const timestamp = moment(timeIso).toISOString()
  const foodID = form.food_id || randomString(14)
  let brandName = 'Quick Macros'
  let entryType: FoodEntry['type'] = 'macro'
  if (form.isAIScan) {
    brandName = form.source === 'voice' ? 'Voice Log' : form.source === 'label' ? 'Label Scan' : 'Food Snap'
    entryType = 'aiscan'
  }
  const postData = stripUndefined({
    id,
    food_id: foodID,
    food_name: form.name,
    brand_name: brandName,
    serving_desc: form.servingsize,
    calories: num(form.calories),
    fats: num(form.fats),
    proteins: num(form.proteins),
    totalcarbs: num(form.totalcarbs),
    fiber: num(form.fiber),
    nutrients: form.nutrients || {},
    type: entryType,
    random: id,
    uid,
    cat: 0,
    date: moment(timestamp).format('YYYY-MM-DD'),
    time: timestamp,
    notes: '',
    image_url: form.image_url || '',
    ...(form.isAIScan ? { isAIScan: true, source: form.source, ingredients: form.ingredients || [] } : {}),
  })
  await setDoc(doc(entriesCol(), id), postData)
  track('log_entry_created', { entry_type: 'food', food_source: form.isAIScan ? `ai-${form.source}` : 'quick-macro', time_of_day: moment(timestamp).format('HH') })
  void updateStreak(uid, timestamp)
  return id
}

/** Copy food entries from one date to another (port of copyDayMeals). */
export async function copyDayMeals(uid: string, sourceDate: string, targetDate: string, entryIds?: string[]): Promise<number> {
  if (sourceDate === targetDate) return 0
  const snap = await getDocs(query(entriesCol(), where('uid', '==', uid), where('date', '==', sourceDate), where('cat', '==', 0)))
  const filter = entryIds && entryIds.length > 0 ? new Set(entryIds) : null
  const docs = filter ? snap.docs.filter((d) => filter.has(d.id) || filter.has((d.data() as FoodEntry).id)) : snap.docs
  if (docs.length === 0) return 0
  let copied = 0
  for (let i = 0; i < docs.length; i += 450) {
    const batch = writeBatch(db)
    for (const d of docs.slice(i, i + 450)) {
      const meal = d.data() as FoodEntry
      const newId = randomString(14)
      const originalTime = moment(meal.time)
      const newTime = originalTime.isValid()
        ? moment(targetDate, 'YYYY-MM-DD').hour(originalTime.hour()).minute(originalTime.minute()).second(originalTime.second()).toISOString()
        : moment(targetDate, 'YYYY-MM-DD').hour(12).minute(0).toISOString()
      batch.set(doc(entriesCol(), newId), { ...meal, id: newId, random: newId, date: targetDate, time: newTime })
      copied++
    }
    await batch.commit()
  }
  await updateStreak(uid, moment(targetDate, 'YYYY-MM-DD').toISOString())
  track('copy_day_meals', { source_date: sourceDate, target_date: targetDate, meals_copied: copied })
  return copied
}

export async function hasFoodLogForDate(uid: string, date: string): Promise<boolean> {
  const snap = await getDocs(query(entriesCol(), where('uid', '==', uid), where('date', '==', date), where('cat', '==', 0), limit(1)))
  return !snap.empty
}

/** Cloud-side date-range read for stats (insights / coach). */
export async function getEntriesInRange(uid: string, start: string, end: string): Promise<LogEntry[]> {
  const snap = await getDocs(query(entriesCol(), where('uid', '==', uid), where('date', '>=', start), where('date', '<=', end)))
  return snap.docs.map((d) => ({ ...(d.data() as LogEntry), id: d.id }))
}

export function catString(cat?: number): string {
  switch (cat) {
    case 0: return 'Meal'
    case 1: return 'Mood / Energy'
    case 2: return 'Symptom'
    case 3: return 'Med / Supplement'
    case 4: return 'Exercise'
    case 5: return 'Water'
    case 6: return 'Notes'
    default: return ''
  }
}

const num = (v: unknown): number => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}
